/**
 * movePool.ts — Push 4 (v1.4.1 b10); full coverage 2026-08-21.
 *
 * Client-side PvP move data used by the detail page's persistent quick /
 * charge move picker and the MoveInsights PvP table.
 *
 * FULL COVERAGE (2026-08-21): pools are now built for EVERY species by
 * joining the raid attackers' Game Master move pools (RAID_ATTACKERS —
 * which already carry elite moves and use the same move ids) onto the
 * generated `combatMove` PvP stats (pvpMoves.generated, the Game Master's
 * own GBL numbers). The LEGACY badge comes from the per-species elite move
 * map generated alongside.
 *
 * The original hand-typed slice below (~19 species / ~50 moves, mirroring
 * the worker-side `gameMasterFallback.ts`) is retained as a FALLBACK for
 * anything the generated join misses. Note the worker's battle-simulator
 * fallback still carries the hand-typed values; regenerate both from the
 * Game Master together if the simulator needs to agree to the digit.
 *
 * FORM AWARE (2026-09-01): the pool is picked for the record's FORM, not just
 * its species, so an Armored Mewtwo gets Psystrike and an Alolan Raichu its
 * own charge moves. The formId -> Game Master form-label matching (and its
 * deliberate fall back to the base row) lives in `attackerForm.ts`. The
 * elite/LEGACY map is keyed by DEX NUMBER, not by form, so it still applies to
 * every form of the species.
 *
 * TWO KINDS OF LEGACY, ONE BADGE (2026-09-01). The Game Master describes the
 * game as it is now, so a move REMOVED from a species' pool in a past
 * rebalance vanishes from it entirely, and a trainer who still owns that
 * Pokémon was shown a pool their own Pokémon was not in. Dialga with Roar of
 * Time was the reported shape. So the pool is a union of two sources:
 *
 *   PVP_ELITE_MOVES   an Elite TM can still teach it. Usually in the Game
 *                     Master pool; a Community Day move PvPoke carries ahead
 *                     of the mined Game Master is NOT, and is unioned in like
 *                     a legacy move (Zoroark's Sucker Punch, 2026-10-10).
 *   PVP_LEGACY_MOVES  NOT in the pool at all; unobtainable, still ownable.
 *                     From PvPoke (MIT), the only source that tracks them.
 *
 * The badge is SPLIT (2026-09-01), because the two answer different questions
 * for a trainer standing in front of a Pokémon: an elite move is one they can
 * still go and get, a legacy move is one they either already have or never
 * will. Flattening both into one word hid the only part that changes what
 * they do next. `MoveMetric.availability` carries the distinction.
 *
 * Both maps are keyed by dex number, per the note above, so a legacy move
 * reaches every form of the species.
 *
 * Non-standard moves are flagged via `availability`. Return / Frustration get
 * dedicated `isPurifiedOnly` / `isShadowOnly` flags so the UI can
 * highlight them when the trainer marks the Pokémon shadow or
 * purified.
 *
 * Pure module. No DOM, no I/O.
 */
import {
  PVP_ELITE_EXTRA_MOVES,
  PVP_ELITE_MOVES,
  PVP_LEGACY_MOVES,
  PVP_MOVES,
} from "./generated/pvpMoves.generated";
import { RAID_MOVES } from "./generated/raidMeta.generated";
import { findRaidAttacker } from "./attackerForm";
import { movePoolAdditionsFor } from "./movePoolAdditions";

interface RawMove {
  moveId: string;
  name: string;
  type: string;
  power: number;
  /** PvP energy delta — negative for fast moves, positive for charge cost. */
  energyDelta: number;
  /** Fast moves only: 500ms ticks the move resolves on. Null for charge. */
  turns: number | null;
}

interface RawSpecies {
  speciesId: number;
  name: string;
  fastMoveIds: string[];
  chargedMoveIds: string[];
}

// Kept in lockstep with cloudflare/src/lib/gameMasterFallback.ts. When the
// worker-side admin route imports the full pvpoke gamemaster these two
// arrays should be regenerated from the same source so worker + client
// agree on every move stat.
const MOVES: RawMove[] = [
  { moveId: "VINE_WHIP",        name: "Vine Whip",        type: "grass",    power: 3,  energyDelta: -8,  turns: 2 },
  { moveId: "TACKLE",           name: "Tackle",           type: "normal",   power: 3,  energyDelta: -3,  turns: 1 },
  { moveId: "EMBER",            name: "Ember",            type: "fire",     power: 7,  energyDelta: -6,  turns: 2 },
  { moveId: "FIRE_FANG",        name: "Fire Fang",        type: "fire",     power: 8,  energyDelta: -8,  turns: 2 },
  { moveId: "WATER_GUN",        name: "Water Gun",        type: "water",    power: 3,  energyDelta: -3,  turns: 1 },
  { moveId: "BUBBLE",           name: "Bubble",           type: "water",    power: 8,  energyDelta: -11, turns: 3 },
  { moveId: "MUD_SHOT",         name: "Mud Shot",         type: "ground",   power: 3,  energyDelta: -9,  turns: 2 },
  { moveId: "DRAGON_BREATH",    name: "Dragon Breath",    type: "dragon",   power: 4,  energyDelta: -3,  turns: 1 },
  { moveId: "DRAGON_TAIL",      name: "Dragon Tail",      type: "dragon",   power: 13, energyDelta: -9,  turns: 3 },
  { moveId: "COUNTER",          name: "Counter",          type: "fighting", power: 8,  energyDelta: -7,  turns: 2 },
  { moveId: "CHARM",            name: "Charm",            type: "fairy",    power: 16, energyDelta: -6,  turns: 3 },
  { moveId: "FAIRY_WIND",       name: "Fairy Wind",       type: "fairy",    power: 3,  energyDelta: -9,  turns: 2 },
  { moveId: "PSYCHO_CUT",       name: "Psycho Cut",       type: "psychic",  power: 3,  energyDelta: -9,  turns: 2 },
  { moveId: "BUG_BITE",         name: "Bug Bite",         type: "bug",      power: 3,  energyDelta: -4,  turns: 1 },
  { moveId: "POUND",            name: "Pound",            type: "normal",   power: 4,  energyDelta: -3,  turns: 1 },
  { moveId: "VOLT_SWITCH",      name: "Volt Switch",      type: "electric", power: 12, energyDelta: -16, turns: 4 },
  { moveId: "THUNDER_SHOCK",    name: "Thunder Shock",    type: "electric", power: 3,  energyDelta: -9,  turns: 2 },
  { moveId: "CONFUSION",        name: "Confusion",        type: "psychic",  power: 16, energyDelta: -12, turns: 4 },
  { moveId: "ICE_SHARD",        name: "Ice Shard",        type: "ice",      power: 5,  energyDelta: -6,  turns: 2 },
  { moveId: "POWDER_SNOW",      name: "Powder Snow",      type: "ice",      power: 6,  energyDelta: -13, turns: 3 },
  { moveId: "ROCK_THROW",       name: "Rock Throw",       type: "rock",     power: 8,  energyDelta: -7,  turns: 2 },
  { moveId: "SHADOW_CLAW",      name: "Shadow Claw",      type: "ghost",    power: 6,  energyDelta: -8,  turns: 2 },
  { moveId: "BITE",             name: "Bite",             type: "dark",     power: 4,  energyDelta: -2,  turns: 1 },
  { moveId: "METAL_CLAW",       name: "Metal Claw",       type: "steel",    power: 5,  energyDelta: -7,  turns: 2 },
  { moveId: "POISON_JAB",       name: "Poison Jab",       type: "poison",   power: 6,  energyDelta: -7,  turns: 2 },
  { moveId: "WING_ATTACK",      name: "Wing Attack",      type: "flying",   power: 5,  energyDelta: -8,  turns: 2 },
  { moveId: "BULLET_PUNCH",     name: "Bullet Punch",     type: "steel",    power: 6,  energyDelta: -7,  turns: 2 },

  { moveId: "FRENZY_PLANT",     name: "Frenzy Plant",     type: "grass",    power: 100, energyDelta: 45, turns: null },
  { moveId: "BLAST_BURN",       name: "Blast Burn",       type: "fire",     power: 110, energyDelta: 50, turns: null },
  { moveId: "HYDRO_CANNON",     name: "Hydro Cannon",     type: "water",    power: 80,  energyDelta: 40, turns: null },
  { moveId: "EARTHQUAKE",       name: "Earthquake",       type: "ground",   power: 120, energyDelta: 65, turns: null },
  { moveId: "DRACO_METEOR",     name: "Draco Meteor",     type: "dragon",   power: 150, energyDelta: 65, turns: null },
  { moveId: "DRAGON_CLAW",      name: "Dragon Claw",      type: "dragon",   power: 50,  energyDelta: 35, turns: null },
  { moveId: "CLOSE_COMBAT",     name: "Close Combat",     type: "fighting", power: 100, energyDelta: 45, turns: null },
  { moveId: "DAZZLING_GLEAM",   name: "Dazzling Gleam",   type: "fairy",    power: 110, energyDelta: 70, turns: null },
  { moveId: "PLAY_ROUGH",       name: "Play Rough",       type: "fairy",    power: 90,  energyDelta: 60, turns: null },
  { moveId: "PSYCHIC",          name: "Psychic",          type: "psychic",  power: 90,  energyDelta: 55, turns: null },
  { moveId: "X_SCISSOR",        name: "X-Scissor",        type: "bug",      power: 65,  energyDelta: 35, turns: null },
  { moveId: "HYPER_BEAM",       name: "Hyper Beam",       type: "normal",   power: 150, energyDelta: 80, turns: null },
  { moveId: "WILD_CHARGE",      name: "Wild Charge",      type: "electric", power: 100, energyDelta: 45, turns: null },
  { moveId: "ICE_BEAM",         name: "Ice Beam",         type: "ice",      power: 90,  energyDelta: 55, turns: null },
  { moveId: "AVALANCHE",        name: "Avalanche",        type: "ice",      power: 90,  energyDelta: 45, turns: null },
  { moveId: "ROCK_SLIDE",       name: "Rock Slide",       type: "rock",     power: 75,  energyDelta: 45, turns: null },
  { moveId: "SHADOW_BALL",      name: "Shadow Ball",      type: "ghost",    power: 100, energyDelta: 55, turns: null },
  { moveId: "CRUNCH",           name: "Crunch",           type: "dark",     power: 70,  energyDelta: 45, turns: null },
  { moveId: "FLASH_CANNON",     name: "Flash Cannon",     type: "steel",    power: 100, energyDelta: 65, turns: null },
  { moveId: "SLUDGE_BOMB",      name: "Sludge Bomb",      type: "poison",   power: 80,  energyDelta: 50, turns: null },
  { moveId: "BRAVE_BIRD",       name: "Brave Bird",       type: "flying",   power: 130, energyDelta: 55, turns: null },
  { moveId: "AERIAL_ACE",       name: "Aerial Ace",       type: "flying",   power: 55,  energyDelta: 45, turns: null },
  // Shadow / Purified status moves.
  { moveId: "RETURN",           name: "Return",           type: "normal",   power: 130, energyDelta: 70, turns: null },
  { moveId: "FRUSTRATION",      name: "Frustration",      type: "normal",   power: 15,  energyDelta: 70, turns: null },
];

const SPECIES: RawSpecies[] = [
  { speciesId: 3,   name: "Venusaur",   fastMoveIds: ["VINE_WHIP"],     chargedMoveIds: ["FRENZY_PLANT", "SLUDGE_BOMB"] },
  { speciesId: 6,   name: "Charizard",  fastMoveIds: ["FIRE_FANG"],     chargedMoveIds: ["BLAST_BURN", "DRAGON_CLAW"] },
  { speciesId: 9,   name: "Blastoise",  fastMoveIds: ["WATER_GUN"],     chargedMoveIds: ["HYDRO_CANNON", "ICE_BEAM"] },
  { speciesId: 25,  name: "Pikachu",    fastMoveIds: ["THUNDER_SHOCK"], chargedMoveIds: ["WILD_CHARGE"] },
  { speciesId: 80,  name: "Slowbro",    fastMoveIds: ["CONFUSION"],     chargedMoveIds: ["PSYCHIC", "ICE_BEAM"] },
  { speciesId: 195, name: "Quagsire",   fastMoveIds: ["MUD_SHOT"],      chargedMoveIds: ["EARTHQUAKE", "SLUDGE_BOMB"] },
  { speciesId: 199, name: "Slowking",   fastMoveIds: ["CONFUSION"],     chargedMoveIds: ["PSYCHIC", "ICE_BEAM"] },
  { speciesId: 230, name: "Kingdra",    fastMoveIds: ["DRAGON_BREATH"], chargedMoveIds: ["HYDRO_CANNON", "DRACO_METEOR"] },
  { speciesId: 242, name: "Blissey",    fastMoveIds: ["POUND"],         chargedMoveIds: ["HYPER_BEAM", "DAZZLING_GLEAM"] },
  { speciesId: 248, name: "Tyranitar",  fastMoveIds: ["BITE"],          chargedMoveIds: ["CRUNCH", "ROCK_SLIDE"] },
  { speciesId: 282, name: "Gardevoir",  fastMoveIds: ["CHARM"],         chargedMoveIds: ["DAZZLING_GLEAM", "PSYCHIC"] },
  { speciesId: 350, name: "Milotic",    fastMoveIds: ["DRAGON_TAIL"],   chargedMoveIds: ["ICE_BEAM", "HYDRO_CANNON"] },
  { speciesId: 376, name: "Metagross",  fastMoveIds: ["BULLET_PUNCH"],  chargedMoveIds: ["EARTHQUAKE", "FLASH_CANNON"] },
  { speciesId: 445, name: "Garchomp",   fastMoveIds: ["DRAGON_TAIL"],   chargedMoveIds: ["EARTHQUAKE", "DRACO_METEOR"] },
  { speciesId: 461, name: "Weavile",    fastMoveIds: ["ICE_SHARD"],     chargedMoveIds: ["AVALANCHE", "X_SCISSOR"] },
  { speciesId: 472, name: "Gliscor",    fastMoveIds: ["WING_ATTACK"],   chargedMoveIds: ["EARTHQUAKE", "X_SCISSOR"] },
  { speciesId: 658, name: "Greninja",   fastMoveIds: ["WATER_GUN"],     chargedMoveIds: ["HYDRO_CANNON", "ICE_BEAM"] },
  { speciesId: 887, name: "Dragapult",  fastMoveIds: ["DRAGON_TAIL"],   chargedMoveIds: ["SHADOW_BALL", "DRAGON_CLAW"] },
];

/**
 * The hand slice's own exclusive moves. All four are Community Day starters'
 * moves, which an Elite TM can still teach, so they are ELITE rather than
 * legacy. The generated path resolves this per species from the two maps; this
 * only covers the ~19-species fallback below.
 */
const ELITE_IDS = new Set<string>(["FRENZY_PLANT", "BLAST_BURN", "HYDRO_CANNON", "DRACO_METEOR"]);

export interface MoveMetric {
  moveId: string;
  name: string;
  type: string;
  power: number;
  energyDelta: number;
  turns: number | null;
  /** Cooldown in ms — PvP timing (turns × 500 for fast; 1500 baseline for charge). */
  cooldownMs: number;
  /** Damage per second (power / cooldown × 1000). */
  dps: number;
  /** Energy per second (|energyDelta| / cooldown × 1000). */
  eps: number;
  /**
   * How a trainer gets this move, when the answer is not "the ordinary way".
   *
   *   "elite"   an Elite TM can still teach it: Community Day moves, raid-day
   *             exclusives. Something they can still act on.
   *   "legacy"  removed from the pool; no longer obtainable at all. Only a
   *             Pokémon caught before it left carries one.
   *
   * Absent on an ordinary move. Both used to read LEGACY, which told a trainer
   * the move was special but not whether they could do anything about it.
   */
  availability?: "elite" | "legacy";
  isPurifiedOnly?: boolean;
  isShadowOnly?: boolean;
  /** Stat-stage effect, e.g. "100%: -1 Atk (foe)". Only on generated data. */
  buff?: string;
}

function fastCooldownMs(turns: number | null): number {
  if (!turns || turns <= 0) return 500;
  return turns * 500;
}

function chargeCooldownMs(): number {
  // PvP charge animations snap to a single shield-decision window.
  return 1500;
}

function deriveMetric(m: RawMove): MoveMetric {
  const isFast = m.energyDelta < 0;
  const cooldownMs = isFast ? fastCooldownMs(m.turns) : chargeCooldownMs();
  const dps = cooldownMs > 0 ? (m.power * 1000) / cooldownMs : 0;
  const eps = cooldownMs > 0 ? (Math.abs(m.energyDelta) * 1000) / cooldownMs : 0;
  return {
    moveId: m.moveId,
    name: m.name,
    type: m.type,
    power: m.power,
    energyDelta: m.energyDelta,
    turns: m.turns ?? null,
    cooldownMs,
    dps: Number(dps.toFixed(2)),
    eps: Number(eps.toFixed(2)),
    availability: ELITE_IDS.has(m.moveId) ? "elite" : undefined,
    isPurifiedOnly: m.moveId === "RETURN" || undefined,
    isShadowOnly: m.moveId === "FRUSTRATION" || undefined,
  };
}

const MOVE_BY_ID: Map<string, MoveMetric> = new Map(MOVES.map((m) => [m.moveId, deriveMetric(m)]));
const SPECIES_BY_ID: Map<number, RawSpecies> = new Map(SPECIES.map((s) => [s.speciesId, s]));

// Generated (Game Master combatMove) metrics — the primary source. Same sign
// convention as the slice, so deriveMetric applies unchanged; the buff string
// rides along for the PvP tables.
const GENERATED_BY_ID: Map<string, MoveMetric> = new Map(
  PVP_MOVES.map((m) => {
    const metric = deriveMetric({
      moveId: m.id,
      name: m.name,
      type: m.type,
      power: m.power,
      energyDelta: m.energyDelta,
      turns: m.turns,
    });
    return [m.id, m.buff ? { ...metric, buff: m.buff } : metric];
  }),
);

/** Prefer the generated Game Master stats; fall back to the hand slice. */
function moveById(id: string): MoveMetric | null {
  return GENERATED_BY_ID.get(id) ?? MOVE_BY_ID.get(id) ?? null;
}

/**
 * Charged moves the Game Master grants OUTSIDE a species' pool (2026-09-04):
 * the Crowned forms' signature moves. They exist as ordinary moves in the
 * PvP table but no Zacian / Zamazenta pool row lists them, so without this
 * the record page's picker could not offer the move a scan just read off
 * the page. Keyed by dex number; offered on the base row and the Crowned
 * row, never on a Hero row, which cannot learn them.
 */
export const SIGNATURE_CHARGED_MOVES: Readonly<Record<number, readonly string[]>> = {
  888: ["BEHEMOTH_BLADE"],
  889: ["BEHEMOTH_BASH"],
};

/** Display names of the signature moves for a species ("Behemoth Bash"). */
export function signatureChargedMoveNames(speciesId: number): string[] {
  return (SIGNATURE_CHARGED_MOVES[speciesId] ?? [])
    .map((id) => moveById(id)?.name)
    .filter((n): n is string => !!n);
}

export interface SpeciesMovePool {
  speciesId: number;
  speciesName: string;
  fast: MoveMetric[];
  charge: MoveMetric[];
  /** True when the dataset does not cover this species. */
  unavailable: boolean;
}

export function getSpeciesMovePool(
  speciesId: number,
  formId?: string | null,
): SpeciesMovePool {
  // Full-coverage path: the raid attackers table carries every released
  // species' Game Master move pool (elite moves included) under the same
  // move ids `combatMove` uses, so join it onto the generated PvP stats.
  // The record's FORM picks the row (Armored Mewtwo, Alolan Raichu, Therian
  // Landorus each carry their own pool); an unmatched or absent form falls
  // back to the base row. See attackerForm.ts.
  const attacker = findRaidAttacker(speciesId, formId);
  if (attacker) {
    // Both maps are keyed by DEX NUMBER, not by form: one entry per species,
    // so the LEGACY badge lands on whichever form's pool carries the move
    // (Armored Mewtwo keeps Psystrike's badge) and a legacy move is offered on
    // every form of the species.
    const legacyIds = PVP_LEGACY_MOVES[speciesId] ?? [];
    const eliteIds = PVP_ELITE_MOVES[speciesId] ?? [];
    // Only the elite moves NO form's Game Master pool carries are unioned;
    // the full elite map is dex-keyed across forms (Armored Mewtwo must not
    // inherit base Mewtwo's Counter, base Marowak not Alolan's Shadow Bone).
    const eliteExtraIds = PVP_ELITE_EXTRA_MOVES[speciesId] ?? [];
    // LEGACY WINS A TIE, and ties do happen: a move can sit in the Game
    // Master's elite list while PvPoke still has it down as legacy, or the
    // other way about, depending on which upstream noticed a change first.
    // Saying "no longer obtainable" about a move a trainer can actually go and
    // get is the cheaper mistake: they try, and find out. The reverse sends
    // them hunting an Elite TM for something that does not exist.
    const availabilityOf = (id: string): "elite" | "legacy" | null =>
      legacyIds.includes(id) ? "legacy" : eliteIds.includes(id) ? "elite" : null;
    const toMetrics = (indices: readonly number[], wantFast: boolean): MoveMetric[] => {
      const out: MoveMetric[] = [];
      for (const i of indices) {
        const id = RAID_MOVES[i]?.id;
        if (!id) continue;
        const m = moveById(id);
        // The kind check is belt-and-braces: PvE-fast and PvP-fast are the
        // same moves, but a stats mismatch must not leak across pools.
        if (!m || (m.energyDelta < 0) !== wantFast) continue;
        const availability = availabilityOf(id) ?? m.availability ?? null;
        out.push(availability && availability !== m.availability ? { ...m, availability } : m);
      }
      return out;
    };
    // A legacy move is absent from the Game Master pool by definition, so it
    // has to be UNIONED in rather than merely badged. Appended, not merged in
    // place: the pool keeps the game's own ordering and the unobtainable
    // moves land at the end where they read as the footnote they are.
    // An ELITE move the Game Master pool lacks is the same hole seen from the
    // other side: PvPoke knows the Community Day pairing weeks before the
    // mined Game Master does (Zoroark's Sucker Punch, 2026-10-10), and an
    // Elite TM teaches it, so it is unioned in the same way, badged ELITE.
    const withLegacy = (pool: MoveMetric[], wantFast: boolean): MoveMetric[] => {
      if (legacyIds.length === 0 && eliteExtraIds.length === 0) return pool;
      const have = new Set(pool.map((m) => m.moveId));
      const extra: MoveMetric[] = [];
      const union = (ids: readonly string[], availability: "legacy" | "elite") => {
        for (const id of ids) {
          if (have.has(id)) continue;
          const m = moveById(id);
          if (!m || (m.energyDelta < 0) !== wantFast) continue;
          have.add(id);
          extra.push({ ...m, availability });
        }
      };
      union(legacyIds, "legacy");
      union(eliteExtraIds, "elite");
      return extra.length > 0 ? [...pool, ...extra] : pool;
    };
    // Pairings the live game has that the shipped Game Master lacks
    // (movePoolAdditions, 2026-09-16): unioned in like legacy moves, appended
    // so the game's own ordering is kept. Redundant once the refresh lands.
    const additions = movePoolAdditionsFor(speciesId);
    const withAdditions = (pool: MoveMetric[], ids: readonly string[], wantFast: boolean): MoveMetric[] => {
      if (ids.length === 0) return pool;
      const have = new Set(pool.map((m) => m.moveId));
      const extra: MoveMetric[] = [];
      for (const id of ids) {
        if (have.has(id)) continue;
        const m = moveById(id);
        if (!m || (m.energyDelta < 0) !== wantFast) continue;
        extra.push(m);
      }
      return extra.length > 0 ? [...pool, ...extra] : pool;
    };
    const fast = withAdditions(withLegacy(toMetrics(attacker.fast, true), true), additions.fast, true);
    let charge = withAdditions(withLegacy(toMetrics(attacker.charged, false), false), additions.charged, false);
    // The signature layer: Crowned Zacian / Zamazenta's Behemoth moves.
    const signature = SIGNATURE_CHARGED_MOVES[speciesId] ?? [];
    if (signature.length > 0 && (attacker.form == null || /crowned/i.test(attacker.form))) {
      const have = new Set(charge.map((m) => m.moveId));
      for (const id of signature) {
        const m = moveById(id);
        if (m && !have.has(id)) charge = [...charge, m];
      }
    }
    if (fast.length > 0 && charge.length > 0) {
      return { speciesId, speciesName: attacker.name, fast, charge, unavailable: false };
    }
  }
  // Fallback: the hand-typed slice (and honest unavailability beyond it).
  const sp = SPECIES_BY_ID.get(speciesId);
  if (!sp) {
    return { speciesId, speciesName: "", fast: [], charge: [], unavailable: true };
  }
  const fast = sp.fastMoveIds.map((id) => MOVE_BY_ID.get(id)).filter((m): m is MoveMetric => !!m);
  const charge = sp.chargedMoveIds.map((id) => MOVE_BY_ID.get(id)).filter((m): m is MoveMetric => !!m);
  return { speciesId, speciesName: sp.name, fast, charge, unavailable: false };
}

export function getMoveMetric(moveId: string | null | undefined): MoveMetric | null {
  if (!moveId) return null;
  return moveById(moveId);
}

/**
 * Layer Return / Frustration onto the charge pool based on the
 * shadow / purified flags. Return only shows when isPurified; Frustration
 * only when isShadow.
 */
export function applyStatusMoves(
  pool: SpeciesMovePool,
  flags: { isShadow: boolean; isPurified: boolean },
): SpeciesMovePool {
  const extras: MoveMetric[] = [];
  if (flags.isPurified) {
    const m = moveById("RETURN");
    if (m) extras.push({ ...m, isPurifiedOnly: true });
  }
  if (flags.isShadow) {
    const m = moveById("FRUSTRATION");
    if (m) extras.push({ ...m, isShadowOnly: true });
  }
  if (extras.length === 0) return pool;
  return {
    ...pool,
    charge: [...extras, ...pool.charge.filter((m) => !extras.some((e) => e.moveId === m.moveId))],
  };
}

export function formatCooldownSeconds(cooldownMs: number): string {
  return `${(cooldownMs / 1000).toFixed(1)}s`;
}
